import React, { useRef, useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, Volume2, VolumeX } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { Reveal } from "../motion/Reveal";
import { SectionKicker } from "./Manifesto";
import { VIDEOS } from "../../lib/data";
import { api } from "../../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VideoCard = ({ index, video, meta, onOpen }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.load();
  }, [video.mp4]);

  const onEnter = () => {
    const v = ref.current;
    if (v) { v.currentTime = 0; v.play().catch(() => {}); }
  };
  const onLeave = () => {
    const v = ref.current;
    if (v) { v.pause(); v.currentTime = 0; }
  };

  return (
    <motion.div
      whileHover="hover"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onClick={() => onOpen(index)}
      className="group relative aspect-video rounded-xl overflow-hidden border border-white/10 cursor-pointer bg-black"
      data-testid={`video-card-${index}`}
    >
      <video
        ref={ref}
        poster={video.poster}
        muted
        loop
        playsInline
        preload="none"
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source src={video.mp4} type="video/mp4" />
        {video.webm && <source src={video.webm} type="video/webm" />}
      </video>
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-black/10 pointer-events-none" />
      <div className="absolute top-4 left-4 text-[11px] font-mono text-lime2 border border-lime2/40 rounded px-2 py-0.5 pointer-events-none">
        0{index + 1}
      </div>
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <motion.span
          variants={{ hover: { scale: 1.15 } }}
          className="grid place-items-center w-16 h-16 rounded-full bg-white/10 backdrop-blur-md border border-white/30 group-hover:bg-emerald2 group-hover:border-emerald2 transition-colors duration-300"
        >
          <Play size={22} className="ml-1 text-white" />
        </motion.span>
      </div>
      <div className="absolute bottom-0 inset-x-0 p-6 pointer-events-none">
        <h3 className="font-display text-xl md:text-2xl font-bold">{meta.t}</h3>
        <p className="text-white/60 text-sm mt-1">{meta.d}</p>
      </div>
    </motion.div>
  );
};

const Lightbox = ({ active, video, meta, close }) => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleMute = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    v.play().catch(() => {});
  };

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={close}
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md grid place-items-center p-4 md:p-10"
      data-testid="video-lightbox"
    >
      <button
        onClick={(e) => { e.stopPropagation(); close(); }}
        data-testid="video-lightbox-close"
        className="absolute top-5 right-5 z-[110] grid place-items-center w-12 h-12 rounded-full border border-white/20 bg-black/50 text-white/80 hover:text-lime2 hover:border-lime2 transition-colors"
      >
        <X size={20} />
      </button>
      <motion.div
        initial={{ scale: 0.94, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.96, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-5xl"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-lime2 text-xs uppercase tracking-[0.25em]">
            <span className="font-mono">0{active + 1}</span>
            <span className="h-px w-6 bg-lime2/50" />
            {meta.t}
          </div>
          <button
            onClick={toggleMute}
            data-testid="video-lightbox-mute"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/70 hover:text-lime2 border border-white/15 hover:border-lime2 rounded-full px-3 py-1.5 transition-colors"
          >
            {muted ? <><VolumeX size={14} /> Tap for sound</> : <><Volume2 size={14} /> Sound on</>}
          </button>
        </div>
        <div className="relative rounded-xl overflow-hidden border border-white/10 aspect-video bg-black">
          <video
            key={active}
            ref={videoRef}
            poster={video.poster}
            controls
            autoPlay
            muted
            playsInline
            className="w-full h-full object-cover"
          >
            <source src={video.mp4} type="video/mp4" />
            {video.webm && <source src={video.webm} type="video/webm" />}
          </video>
        </div>
        <p className="mt-3 text-white/50 text-sm">{meta.d}</p>
      </motion.div>
    </motion.div>,
    document.body
  );
};

export const StoryVideos = () => {
  const { t } = useLang();
  const [active, setActive] = useState(null);
  const [films, setFilms] = useState([]);

  useEffect(() => {
    api.get("/films").then((r) => setFilms(r.data)).catch(() => {});
  }, []);

  const resolved = VIDEOS.map((v, i) => {
    const f = films[i];
    const poster = f && f.has_poster ? `${BACKEND_URL}${f.poster_url}` : v.poster;
    if (f && f.has_upload) return { mp4: `${BACKEND_URL}${f.url}`, poster };
    return { ...v, poster };
  });

  const open = useCallback((i) => setActive(i), []);
  const close = useCallback(() => setActive(null), []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  return (
    <section id="story" className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32" data-testid="story-section">
      <Reveal>
        <SectionKicker>{t.story.kicker}</SectionKicker>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <h2 className="font-display font-extrabold tracking-tighter text-4xl md:text-6xl max-w-2xl leading-[0.95]">
            {t.story.title}
          </h2>
          <p className="text-white/60 max-w-sm text-sm md:text-base">{t.story.sub}</p>
        </div>
      </Reveal>

      <div className="mt-14 grid sm:grid-cols-2 gap-5">
        {t.story.videos.map((v, i) => (
          <Reveal key={i} delay={(i % 2) * 0.1}>
            <VideoCard index={i} video={resolved[i]} meta={v} onOpen={open} />
          </Reveal>
        ))}
      </div>

      <AnimatePresence>
        {active !== null && (
          <Lightbox active={active} video={resolved[active]} meta={t.story.videos[active]} close={close} />
        )}
      </AnimatePresence>
    </section>
  );
};
